/* Xiaomi 2026 · Gift Operations Kanban · Kavita's surface
 *
 * /xiaomi/gift-ops
 *
 * Six-stage Kanban matching v4 workflow steps 1-6:
 *   approval_pending → ordered → awaiting_ready
 *     → ready_to_dispatch → couriered → at_dbr_depot (handed to ZSM/DBR)
 *
 * Card per partner shows: outlet · owner · spouse · tier chip · zone · DBR depot ·
 * language · catalogue label · current courier/AWB if set.
 *
 * Click card → side drawer with full record + advance-stage form.
 *
 * Visual: Ayush's design language adopted · Fraunces headers · Mi-orange
 * accents · tier chips with semantic colour · spacious card composition.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import JourneyTimeline from '../components/XiaomiJourneyTimeline';
import MessageComposerModal from '../components/xiaomi/MessageComposerModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const STAGE_META = {
  approval_pending:    { label: 'Approval pending',   icon: '⏳', tone: '#92400e', bg: '#fef3c7', border: '#fcd34d' },
  ordered:             { label: 'Ordered',            icon: '📦', tone: '#1e40af', bg: '#dbeafe', border: '#93c5fd' },
  awaiting_ready:      { label: 'Awaiting ready',     icon: '🔔', tone: '#5b21b6', bg: '#ede9fe', border: '#c4b5fd' },
  ready_to_dispatch:   { label: 'Ready to dispatch',  icon: '✅', tone: '#0f766e', bg: '#ccfbf1', border: '#5eead4' },
  couriered:           { label: 'Couriered',          icon: '🚚', tone: '#7c2d12', bg: '#fed7aa', border: '#fb923c' },
  at_dbr_depot:        { label: 'At DBR depot',       icon: '🏢', tone: '#166534', bg: '#dcfce7', border: '#86efac' },
};

const TIER_CHIP = {
  'Platinum Luxe':      { bg: '#fef3c7', border: '#ca8a04', tone: '#713f12' },
  'Platinum Signature': { bg: '#fae8ff', border: '#a855f7', tone: '#581c87' },
  'Platinum Prime':     { bg: '#dbeafe', border: '#3b82f6', tone: '#1e3a8a' },
  'Rising Platinum':    { bg: '#dcfce7', border: '#16a34a', tone: '#14532d' },
};


function TierChip({ tier }) {
  const c = TIER_CHIP[tier] || { bg: '#f3f4f6', border: '#9ca3af', tone: '#374151' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, color: c.tone }}
      data-testid="tier-chip"
    >
      {tier?.replace('Platinum ', '').replace('Rising', 'Rising')}
    </span>
  );
}


function GiftOpsCard({ record, onClick, selected, onToggleSelect }) {
  return (
    <div
      className="relative w-full bg-white rounded-lg border p-3 mb-2 hover:shadow-md transition-shadow"
      style={{ borderColor: selected ? '#FF6900' : '#e5e1dc',
               borderWidth: selected ? 2 : 1,
               background: selected ? '#FFF7ED' : 'white' }}
      data-testid={`gift-ops-card-${record._id}`}
    >
      <input
        type="checkbox"
        checked={!!selected}
        onChange={(e) => { e.stopPropagation(); onToggleSelect(record); }}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-2 right-2 w-4 h-4 cursor-pointer"
        style={{ accentColor: '#FF6900' }}
        title="Select for bulk actions"
        data-testid={`gift-ops-card-select-${record._id}`}
      />
      <button onClick={() => onClick(record)} className="w-full text-left pr-6 cursor-pointer">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-soft">{record.dealer_id}</div>
          <div className="text-sm font-semibold text-[#15110d] truncate" title={record.outlet_name}>
            {record.outlet_name}
          </div>
        </div>
        <TierChip tier={record.tier} />
      </div>
      <div className="text-[12px] text-[#374151] truncate">{record.owner_name}</div>
      <div className="text-[11px] text-soft mt-1.5 flex items-center gap-2 flex-wrap">
        <span>📍 {record.city}</span>
        <span>·</span>
        <span>🏢 {record.dbr_name || '—'}</span>
        {record.language_pref && (
          <>
            <span>·</span>
            <span className="uppercase">{record.language_pref}</span>
          </>
        )}
      </div>
      {record.courier_name && (
        <div className="text-[11px] mt-1.5 px-2 py-1 bg-[#f3f4f6] rounded font-mono">
          🚚 {record.courier_name} · {record.awb_number || 'no AWB'}
        </div>
      )}
      <div className="text-[11px] text-soft mt-1 truncate">{record.catalogue_label}</div>
      </button>
    </div>
  );
}


function StageColumn({ stage, records, onCardClick, selectedIds, onToggleSelect, onSelectAllInStage }) {
  const meta = STAGE_META[stage];
  const inStageSelected = records.filter((r) => selectedIds.has(r._id)).length;
  const allInStageSelected = records.length > 0 && inStageSelected === records.length;
  return (
    <div
      className="flex-1 min-w-[260px] rounded-lg overflow-hidden"
      style={{ border: `1px solid ${meta.border}` }}
      data-testid={`stage-column-${stage}`}
    >
      <div
        className="px-3 py-2 font-semibold text-[12px] flex items-center justify-between"
        style={{ backgroundColor: meta.bg, color: meta.tone, borderBottom: `1px solid ${meta.border}` }}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={allInStageSelected}
            onChange={() => onSelectAllInStage(stage, records, !allInStageSelected)}
            disabled={records.length === 0}
            className="w-3.5 h-3.5 cursor-pointer disabled:opacity-30"
            style={{ accentColor: '#FF6900' }}
            title="Select all in this column"
            data-testid={`stage-select-all-${stage}`}
          />
          <span>{meta.icon} {meta.label}</span>
        </div>
        <span data-testid={`stage-count-${stage}`}>
          {inStageSelected > 0 ? `${inStageSelected}/${records.length}` : records.length}
        </span>
      </div>
      <div className="p-2 bg-[#faf6ef] min-h-[400px] max-h-[700px] overflow-y-auto">
        {records.length === 0 ? (
          <div className="text-center text-[11px] text-soft py-10">
            (empty)
          </div>
        ) : (
          records.map((r) => (
            <GiftOpsCard key={r._id} record={r}
              onClick={onCardClick}
              selected={selectedIds.has(r._id)}
              onToggleSelect={onToggleSelect} />
          ))
        )}
      </div>
    </div>
  );
}


function AdvanceDrawer({ record, onClose, onAdvanced }) {
  const [fullRecord, setFullRecord] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formData, setFormData] = useState({});
  const [noteText, setNoteText] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);

  const loadFull = useCallback(() => {
    if (!record) return;
    axios.get(`${API}/xiaomi/gift-ops/${record._id}`, auth())
      .then((r) => setFullRecord(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Load failed'));
  }, [record]);

  useEffect(() => { loadFull(); }, [loadFull]);

  if (!record) return null;
  const stages = ['approval_pending', 'ordered', 'awaiting_ready',
                  'ready_to_dispatch', 'couriered', 'at_dbr_depot'];
  const currentIdx = stages.indexOf(record.stage);
  const nextStage = currentIdx >= 0 && currentIdx < stages.length - 1
    ? stages[currentIdx + 1] : null;

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setBusy(true);
    try {
      await axios.post(`${API}/xiaomi/gift-ops/${record._id}/note`,
        { text }, auth());
      toast.success('Note saved');
      setNoteText('');
      loadFull();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save note');
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(
      `Delete this gift-ops record?\n\n${record.outlet_name} · ${record.benefit_id}\n\n`
      + 'Use this only to scrub mistakes (e.g. wrong tier, duplicate, partner archived). '
      + 'This cannot be undone (logged to xiaomi_gift_ops_audit).',
    )) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/xiaomi/gift-ops/${record._id}`, auth());
      toast.success('Record deleted');
      onAdvanced();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const handleAdvance = async () => {
    if (!nextStage) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/xiaomi/gift-ops/${record._id}/advance`,
        { to_stage: nextStage, ...formData },
        auth(),
      );
      toast.success(`Advanced → ${STAGE_META[nextStage].label}`);
      onAdvanced();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Advance failed');
    } finally { setBusy(false); }
  };

  const renderFormFields = () => {
    if (!nextStage) return null;
    if (nextStage === 'ordered') {
      return (
        <div className="space-y-3">
          <FormField label="Supplier name" testId="field-supplier-name"
            value={formData.supplier_name || ''}
            onChange={(v) => setFormData({ ...formData, supplier_name: v })}
            placeholder={fullRecord?.catalogue_supplier_default} />
          <FormField label="PO reference" testId="field-supplier-po-ref"
            value={formData.supplier_po_ref || ''}
            onChange={(v) => setFormData({ ...formData, supplier_po_ref: v })} />
          <FormField label="Expected ready date (YYYY-MM-DD)" testId="field-expected-ready"
            value={formData.expected_ready_date || ''}
            onChange={(v) => setFormData({ ...formData, expected_ready_date: v })} />
        </div>
      );
    }
    if (nextStage === 'ready_to_dispatch') {
      return (
        <div className="space-y-3">
          <FormField label="Packed count" testId="field-packed-count"
            value={formData.packed_count || ''}
            onChange={(v) => setFormData({ ...formData, packed_count: parseInt(v, 10) || 0 })} />
          <FormField label="QC notes (optional)" testId="field-qc-notes"
            value={formData.qc_notes || ''}
            onChange={(v) => setFormData({ ...formData, qc_notes: v })}
            multiline />
        </div>
      );
    }
    if (nextStage === 'couriered') {
      const shipTo = formData.ship_to_type || 'dbr';
      return (
        <div className="space-y-3">
          <FormField label="Courier name" testId="field-courier-name"
            value={formData.courier_name || ''}
            onChange={(v) => setFormData({ ...formData, courier_name: v })}
            placeholder={fullRecord?.catalogue_courier_default} />
          <FormField label="AWB / tracking number" testId="field-awb-number"
            value={formData.awb_number || ''}
            onChange={(v) => setFormData({ ...formData, awb_number: v })} />
          <FormField label="Tracking URL (Delhivery / BlueDart / Shiprocket)" testId="field-tracking-url"
            value={formData.tracking_url || ''}
            onChange={(v) => setFormData({ ...formData, tracking_url: v })}
            placeholder="https://…/track/AWB" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-soft mb-1">Ship-to address</div>
            <div className="flex gap-1 mb-2" data-testid="ship-to-selector">
              {[
                { id: 'dealer', label: '🏪 Dealer outlet' },
                { id: 'dbr',    label: '🚚 DBR depot' },
                { id: 'custom', label: '✏️ Custom' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setFormData({ ...formData, ship_to_type: opt.id })}
                  className="px-3 py-1 rounded-full text-[11px] font-semibold border transition"
                  style={{
                    borderColor: shipTo === opt.id ? '#ff6900' : '#e6dccd',
                    background: shipTo === opt.id ? '#ff6900' : '#fff',
                    color: shipTo === opt.id ? '#fff' : '#15110d',
                  }}
                  data-testid={`ship-to-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <FormField label={shipTo === 'custom' ? 'Custom address' : `${shipTo === 'dealer' ? 'Dealer' : 'DBR'} address (pre-filled, can override)`}
              testId="field-ship-to-address"
              value={formData.ship_to_address || (shipTo === 'dealer'
                ? [fullRecord?.dealer_address, fullRecord?.city].filter(Boolean).join(', ')
                : shipTo === 'dbr'
                  ? [fullRecord?.dbr_name, fullRecord?.zone].filter(Boolean).join(' · ')
                  : '')}
              onChange={(v) => setFormData({ ...formData, ship_to_address: v })}
              multiline
              placeholder={shipTo === 'custom' ? 'Type the destination address…' : ''} />
          </div>
          <FormField label="ETA at destination (optional)" testId="field-eta-at-dbr"
            value={formData.eta_at_dbr || ''}
            onChange={(v) => setFormData({ ...formData, eta_at_dbr: v })} />
          <FormField label="Dispatch photo URL (optional)" testId="field-dispatch-photo"
            value={formData.dispatch_photo_url || ''}
            onChange={(v) => setFormData({ ...formData, dispatch_photo_url: v })}
            placeholder="Paste Cloudinary/Drive URL of the packed gift" />
        </div>
      );
    }
    if (nextStage === 'at_dbr_depot') {
      return (
        <div className="space-y-3">
          <FormField label="DBR received count" testId="field-dbr-received-count"
            value={formData.dbr_received_count || ''}
            onChange={(v) => setFormData({ ...formData, dbr_received_count: parseInt(v, 10) || 0 })} />
          <FormField label="DBR handover photo URL (optional · at depot)" testId="field-dbr-handover-photo"
            value={formData.dbr_handover_photo_url || ''}
            onChange={(v) => setFormData({ ...formData, dbr_handover_photo_url: v })}
            placeholder="Photo of depot signing receipt" />
          <FormField label="Final handover photo URL (optional · with dealer)" testId="field-handover-photo"
            value={formData.handover_photo_url || ''}
            onChange={(v) => setFormData({ ...formData, handover_photo_url: v })}
            placeholder="Photo of dealer with gift · if ZSM already sent one" />
        </div>
      );
    }
    if (nextStage === 'delivered_to_dealer') {
      return (
        <div className="space-y-3">
          <FormField label="POD URL(s) · comma-separated · proof-of-delivery photos"
            testId="field-pod-urls"
            value={(formData.pod_urls || []).join(', ')}
            onChange={(v) => setFormData({
              ...formData,
              pod_urls: v.split(',').map((s) => s.trim()).filter(Boolean),
            })}
            placeholder="https://res.cloudinary.com/…, https://…"
            multiline />
          <FormField label="Signed by (name)" testId="field-pod-signed-by"
            value={formData.pod_signed_by || ''}
            onChange={(v) => setFormData({ ...formData, pod_signed_by: v })} />
          <FormField label="Delivered at (YYYY-MM-DD HH:MM)" testId="field-delivered-at"
            value={formData.delivered_at || ''}
            onChange={(v) => setFormData({ ...formData, delivered_at: v })} />
        </div>
      );
    }
    return <div className="text-sm text-soft">No additional data needed.</div>;
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 flex justify-end"
      onClick={onClose}
      data-testid="gift-ops-drawer"
    >
      <div
        className="bg-white w-[480px] max-w-full h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="gift-ops-drawer-panel"
      >
        <div className="p-6 border-b border-line">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-soft">
                {record.benefit_id}
              </div>
              <Link
                to={`/xiaomi/partners/${record.dealer_id}`}
                className="text-xl font-semibold mt-0.5 hover:underline block"
                style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}
                data-testid="drawer-partner-link"
                title="Open 360° partner profile"
              >
                {record.outlet_name} ↗
              </Link>
              <div className="text-sm text-[#374151] mt-1">{record.owner_name}</div>
              <div className="flex gap-2 mt-2"><TierChip tier={record.tier} /></div>
            </div>
            <button onClick={onClose} className="text-2xl text-soft hover:text-ink"
              data-testid="drawer-close">×</button>
          </div>

          {/* 02-Jun-2026 — Per Dipali's doctrine: NEVER fire WhatsApp direct.
              Compose → translate → push to Zoho (Tanisha's team fires WA). */}
          <button
            onClick={() => setComposerOpen(true)}
            className="mt-4 w-full px-3 py-2 rounded-md text-sm font-semibold text-white flex items-center justify-center gap-2 hover:opacity-90"
            style={{ background: '#128c7e' }}
            data-testid="drawer-compose-zoho"
          >
            ✍️ Compose & push to Zoho
          </button>
        </div>

        {fullRecord && (
          <>
            <div className="p-6 border-b border-line">
              <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
                Partner context
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Row label="DBR depot" value={fullRecord.dbr_name} />
                <Row label="City" value={fullRecord.city} />
                <Row label="State" value={fullRecord.state} />
                <Row label="Zone" value={fullRecord.zone} />
                <Row label="Spouse" value={fullRecord.spouse_name || '—'} />
                <Row label="Language" value={fullRecord.language_pref} />
              </dl>
            </div>

            <div className="p-6 border-b border-line">
              <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
                Catalogue (internal · surprise for partner)
              </div>
              <div className="text-sm font-medium text-[#15110d]">{fullRecord.catalogue_label}</div>
              <ul className="text-[13px] text-[#374151] mt-2 space-y-1 list-disc list-inside">
                {(fullRecord.catalogue_contents || []).map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
              <div className="text-[11px] text-soft mt-3">
                Cost band: {fullRecord.catalogue_cost_band_inr || '—'} INR
                {' · '}
                Supplier default: {fullRecord.catalogue_supplier_default || '—'}
              </div>
            </div>

            <div className="p-6 border-b border-line">
              <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
                Stage history
              </div>
              {(fullRecord.stage_history || []).length === 0 ? (
                <div className="text-sm text-soft">No transitions yet.</div>
              ) : (
                <ol className="text-[13px] space-y-2">
                  {fullRecord.stage_history.map((h, i) => (
                    <li key={i} className="border-l-2 border-[#ff6900] pl-3">
                      <div className="font-medium">{h.from_stage} → {h.to_stage}</div>
                      <div className="text-[11px] text-soft">{h.ts} · {h.actor}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}

        {nextStage ? (
          <div className="p-6">
            <div className="text-[11px] uppercase tracking-wider text-soft mb-3">
              Advance to: {STAGE_META[nextStage].label}
            </div>
            {renderFormFields()}
            <button
              onClick={handleAdvance} disabled={busy}
              className="w-full mt-4 px-4 py-3 rounded-lg bg-[#ff6900] text-white font-semibold hover:opacity-90 disabled:opacity-50"
              data-testid="advance-stage-btn"
            >
              {busy ? 'Working…' : `→ ${STAGE_META[nextStage].label}`}
            </button>
          </div>
        ) : (
          <div className="p-6 text-sm text-soft text-center">
            ✓ Reached final stage · DBR/ZSM hands off to dealer next.
          </div>
        )}

        {/* Full 15-step partner journey (data-driven by benefit type) */}
        {fullRecord && (
          <div className="p-6 border-t border-line bg-[#fffdf8]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-soft">
                15-step Partner Journey
              </div>
              <Link
                to={`/xiaomi/partners/${fullRecord.dealer_id}/journey/${fullRecord.benefit_id}/${fullRecord.month_ym}`}
                className="text-[10px] underline"
                style={{ color: '#9A4B0E' }}
                data-testid="open-full-journey-link"
              >
                Open shareable page →
              </Link>
            </div>
            <JourneyTimeline
              retailerId={fullRecord.dealer_id}
              benefitId={fullRecord.benefit_id}
              monthYm={fullRecord.month_ym}
              compact={false}
              onChange={loadFull}
            />
          </div>
        )}

        {/* Notes log — free-text context that doesn't fit the stage workflow */}
        {fullRecord && (
          <div className="p-6 border-t border-line">
            <div className="text-[11px] uppercase tracking-wider text-soft mb-3">
              Notes ({(fullRecord.notes || []).length})
            </div>
            <div className="space-y-2 mb-3 max-h-44 overflow-y-auto">
              {(fullRecord.notes || []).length === 0 ? (
                <div className="text-[12px] text-soft italic">No notes yet.</div>
              ) : (
                fullRecord.notes.map((n, i) => (
                  <div key={i} className="bg-[#fff9eb] border border-[#fde68a] rounded p-2 text-[12px]">
                    <div className="text-[10px] text-soft mb-0.5">
                      {n.by || '?'} · {new Date(n.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </div>
                    <div className="text-[#15110d] whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <input type="text" value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }}
                placeholder="Add a note (vendor said X, partner asked for Y…)"
                className="flex-1 px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
                data-testid="add-note-input" />
              <button onClick={handleAddNote} disabled={busy || !noteText.trim()}
                className="px-3 py-2 rounded bg-[#15110d] text-[#f3e6cf] text-sm hover:opacity-90 disabled:opacity-40"
                data-testid="add-note-btn">
                + Note
              </button>
            </div>
          </div>
        )}

        {/* Danger zone — delete (admin only via backend, button shown for everyone */}
        {/* and the API returns 403 for non-admins; toast bubbles it cleanly). */}
        <div className="p-6 border-t border-line">
          <button onClick={handleDelete} disabled={busy}
            className="text-[12px] text-[#dc2626] hover:underline disabled:opacity-50"
            data-testid="delete-record-btn"
          >
            🗑 Delete this record (admin only)
          </button>
        </div>
      </div>

      {/* Compose-via-Zoho modal · Doctrine: never fire WhatsApp direct */}
      {composerOpen && (
        <MessageComposerModal
          dealerId={record.dealer_id}
          dealerLanguage={record.language_pref}
          benefitName={record.catalogue_label || record.benefit_id}
          initialSource={`Hi ${record.owner_name?.split(' ')[0] || 'Sir'}, regarding your ${record.catalogue_label || record.benefit_id} for Xiaomi Platinum 2026…\n\n— Kavita, Les Concierges`}
          onClose={() => setComposerOpen(false)}
          onSaved={() => {
            toast.success('Pushed to Zoho · Tanisha\'s team fires WhatsApp');
            setComposerOpen(false);
          }}
        />
      )}
    </div>
  );
}


function FormField({ label, value, onChange, placeholder, multiline, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] text-soft mb-1">{label}</div>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={3}
          className="w-full px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
          data-testid={testId} />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded border border-line text-sm focus:outline-none focus:border-[#ff6900]"
          data-testid={testId} />
      )}
    </label>
  );
}


function Row({ label, value }) {
  return (
    <>
      <dt className="text-[11px] text-soft uppercase tracking-wider">{label}</dt>
      <dd className="text-sm text-[#15110d]">{value || '—'}</dd>
    </>
  );
}


export default function XiaomiGiftOps() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filterBenefit, setFilterBenefit] = useState('');
  const [filterTier, setFilterTier] = useState('');
  // Bulk multi-select state · Set of record _ids
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // null | 'advance' | 'journey'

  const toggleSelect = useCallback((rec) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(rec._id)) next.delete(rec._id); else next.add(rec._id);
      return next;
    });
  }, []);
  const selectAllInStage = useCallback((_stage, records, on) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      for (const r of records) {
        if (on) next.add(r._id); else next.delete(r._id);
      }
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBenefit) params.set('benefit_id', filterBenefit);
      if (filterTier) params.set('tier', filterTier);
      const r = await axios.get(`${API}/xiaomi/gift-ops/kanban?${params}`, auth());
      setSnapshot(r.data);
    } catch (e) {
      toast.error('Failed to load gift-ops kanban');
    } finally { setLoading(false); }
  }, [filterBenefit, filterTier]);

  useEffect(() => { load(); }, [load]);

  const totalCount = useMemo(() => {
    if (!snapshot) return 0;
    return Object.values(snapshot.counts).reduce((a, b) => a + b, 0);
  }, [snapshot]);

  return (
    <div className="xiaomi-theme p-8 space-y-6" data-testid="gift-ops-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            Gift Operations
          </h1>
          <p className="text-sm text-soft mt-1">
            Kavita's workbench · 6-stage workflow from Xiaomi approval to DBR depot handover
            {totalCount > 0 && ` · ${totalCount} records across all stages`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={async () => {
              if (!window.confirm(
                'Catch up Jan→May 2026 backlog?\n\n'
                + 'Materialises birthday + anniversary + spouse gifts for partners '
                + 'whose dates fell in Jan–May 2026 (the months we missed because '
                + 'the programme launched late). Idempotent — safe to re-run.',
              )) return;
              try {
                const r = await axios.post(`${API}/xiaomi/gift-ops/materialise-range`,
                  { from_month: '2026-01', to_month: '2026-05' },
                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                const rep = r.data.report;
                const monthLines = rep.months
                  .filter((m) => m.created + m.already_present > 0)
                  .map((m) => `  ${m.month_ym}: ${m.created} new`)
                  .join('\n');
                toast.success(
                  `Backlog caught up · ${rep.total_created} new records `
                  + `across ${rep.months_scanned} months`,
                );
                /* eslint-disable no-console */
                console.log('[gift-ops] backlog report:\n' + monthLines);
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Backlog catch-up failed');
              }
            }}
            className="px-4 py-2 rounded-lg bg-white border text-sm hover:bg-[#fef3c7]"
            style={{ borderColor: '#ca8a04', color: '#713f12' }}
            title="Materialise Jan–May 2026 birthday + anniversary gift records (we launched in June, this catches up the backlog)"
            data-testid="materialise-backlog-btn"
          >
            ⏪ Catch up Jan→May backlog
          </button>
          <button
            onClick={async () => {
              if (!window.confirm('Materialise gift records for the current month?\n\n'
                + 'Creates new Approval-Pending cards for every eligible partner who '
                + 'is due a gift this month. Safe to re-run — already-created records '
                + 'are left alone (idempotent).')) return;
            try {
              const r = await axios.post(`${API}/xiaomi/gift-ops/materialise-month`,
                  {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
              const rep = r.data.report;
              toast.success(
                `Materialised · ${rep.created} new · ${rep.already_present} already existed `
                + `· covers ${rep.due_benefits.length} benefit(s)`,
              );
              load();
            } catch (e) {
              toast.error(e?.response?.data?.detail || 'Materialise failed');
            }
            }}
            className="px-4 py-2 rounded-lg bg-white border text-sm hover:bg-[#faf6ef]"
            style={{ borderColor: '#fb923c', color: '#7c2d12' }}
            title="Auto-runs on the 1st of every month at 07:30 IST · this button fires it manually"
            data-testid="materialise-month-btn"
          >
            ✨ Materialise this month
          </button>
          <a
            href={`${API}/xiaomi/gift-ops/export.csv`}
            className="px-4 py-2 rounded-lg bg-white border border-line text-sm text-[#15110d] hover:bg-[#faf6ef]"
            data-testid="export-csv-btn"
          >
            ⬇ CSV
          </a>
          <label
            className="px-4 py-2 rounded-lg bg-white border border-line text-sm text-[#15110d] hover:bg-[#faf6ef] cursor-pointer"
            data-testid="import-csv-btn"
            title="Bulk update or create gift-ops records via CSV. Rows with record_id update existing; rows with dealer_id+benefit_id+month_ym create new (eligibility-checked)."
          >
            ⬆ Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden"
              data-testid="import-csv-file"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const form = new FormData();
                form.append('file', f);
                try {
                  const r = await axios.post(
                    `${API}/xiaomi/gift-ops/import.csv`, form,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
                                 'Content-Type': 'multipart/form-data' } });
                  const d = r.data;
                  toast.success(
                    `Imported · ${d.created} created · ${d.updated} updated · ${d.errors} error(s)`,
                    { duration: 6000 });
                  if (d.errors > 0) {
                    /* eslint-disable no-console */
                    console.warn('[gift-ops import] errors:', d.samples?.errors);
                  }
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Import failed');
                }
              }} />
          </label>
          <label
            className="px-4 py-2 rounded-lg bg-white border border-line text-sm text-[#15110d] hover:bg-[#faf6ef] cursor-pointer"
            data-testid="bulk-journey-csv-btn"
            title="Bulk-tick 15-step journey steps from a CSV. Columns: retailer_id,benefit_id,month_ym,step,note · up to 5,000 rows."
          >
            🛣 Bulk-tick Step CSV
            <input type="file" accept=".csv,text/csv" className="hidden"
              data-testid="bulk-journey-csv-file"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const form = new FormData();
                form.append('file', f);
                try {
                  const r = await axios.post(
                    `${API}/xiaomi/journey/bulk-advance.csv`, form,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
                                 'Content-Type': 'multipart/form-data' } });
                  const d = r.data;
                  toast.success(
                    `Bulk-ticked · ${d.marked} steps marked · ${d.errors} error(s)`,
                    { duration: 6000 });
                  if (d.errors > 0) {
                    /* eslint-disable no-console */
                    console.warn('[bulk-journey] errors:', d.samples?.errors);
                  }
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Bulk-tick failed');
                }
              }} />
          </label>
          <label
            className="px-4 py-2 rounded-lg bg-[#ecfdf5] border border-[#a7f3d0] text-sm text-[#065F46] hover:bg-[#d1fae5] cursor-pointer"
            data-testid="bulk-dispatch-csv-btn"
            title="Upload courier manifest CSV (retailer_id, benefit_id, month_ym, awb_number, tracking_url, courier_name, ship_to_type, note). One click fires every row to stage=couriered, bumps journey to step 8, and posts the AWB + tracking link as an internal comment on each Zoho ticket."
          >
            🚚 Bulk-dispatch CSV
            <input type="file" accept=".csv,text/csv" className="hidden"
              data-testid="bulk-dispatch-csv-file"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                const form = new FormData();
                form.append('file', f);
                // First, dry-run so Kavita can confirm the count
                try {
                  const pre = await axios.post(
                    `${API}/xiaomi/gift-ops/bulk-dispatch.csv?dry_run=true`, form,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
                                 'Content-Type': 'multipart/form-data' } });
                  const dr = pre.data;
                  const msg = `Dry-run · ${dr.dispatched} would dispatch · `
                    + `${dr.skipped} skipped · ${dr.errors} error(s).\n\n`
                    + (dr.errors > 0
                      ? 'ERRORS:\n' + (dr.samples?.errors || []).slice(0, 5)
                          .map((e) => `  row ${e.row}: ${e.error}`).join('\n')
                          + '\n\n'
                      : '')
                    + 'Apply now? (cannot be undone)';
                  if (!window.confirm(msg)) return;
                  const form2 = new FormData();
                  form2.append('file', f);
                  const r = await axios.post(
                    `${API}/xiaomi/gift-ops/bulk-dispatch.csv`, form2,
                    { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`,
                                 'Content-Type': 'multipart/form-data' } });
                  const d = r.data;
                  toast.success(
                    `Dispatched · ${d.dispatched} · Zoho comments: ${d.zoho_comments_posted} · errors: ${d.errors}`,
                    { duration: 6000 });
                  /* eslint-disable no-console */
                  if (d.errors > 0) console.warn('[bulk-dispatch] errors:', d.samples?.errors);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Bulk-dispatch failed');
                }
              }} />
          </label>
          <a
            href={`${API}/xiaomi/gift-ops/bulk-dispatch-template.csv`}
            className="px-2 py-2 rounded-lg bg-white border border-line text-xs text-soft hover:bg-[#faf6ef]"
            data-testid="bulk-dispatch-template-btn"
            title="Download blank template for the bulk-dispatch CSV"
          >
            ⇩ template
          </a>
          <button onClick={load}
            className="px-4 py-2 rounded-lg bg-[#15110d] text-[#f3e6cf] text-sm hover:opacity-90"
            data-testid="refresh-btn"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <select value={filterBenefit} onChange={(e) => setFilterBenefit(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-benefit">
          <option value="">All gift benefits</option>
          <option value="xiaomi_b01_onboarding_gift">Onboarding Gift (b01)</option>
          <option value="xiaomi_b02_welcome_certificate_rising">Welcome Certificate · Rising (b02)</option>
          <option value="xiaomi_b03_luxury_kit_for_spouse">Luxury Spouse Kit (b03)</option>
          <option value="xiaomi_b06_birthday_gift">Birthday Gift (b06)</option>
          <option value="xiaomi_b12_parent_s_gift">Parent's Gift (b12)</option>
          <option value="xiaomi_b13_gift_for_kids">Gift for Kids (b13)</option>
          <option value="xiaomi_b15_diwali_gift">Diwali Gift (b15)</option>
          <option value="xiaomi_b16_year_end_gift">Year End Gift (b16)</option>
        </select>
        <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-tier">
          <option value="">All tiers</option>
          <option value="Platinum Luxe">Platinum Luxe</option>
          <option value="Platinum Signature">Platinum Signature</option>
          <option value="Platinum Prime">Platinum Prime</option>
          <option value="Rising Platinum">Rising Platinum</option>
        </select>
      </div>

      {/* Empty state */}
      {!loading && totalCount === 0 && (
        <div className="rounded-lg border border-dashed border-line p-12 text-center bg-white"
          data-testid="gift-ops-empty">
          <div className="text-4xl mb-2">📦</div>
          <div className="text-sm font-semibold text-[#15110d]">No gift-ops records yet</div>
          <p className="text-[12px] text-soft mt-1.5 max-w-md mx-auto">
            Records auto-materialise on the 1st of every month at 07:30 IST.
            Click <strong>"✨ Materialise this month"</strong> above to fire it manually
            (idempotent · safe to run multiple times).
          </p>
          <div className="text-[12px] text-soft mt-1">
            Records auto-materialise on the 1st of every month at 07:30 IST.
          </div>
        </div>
      )}

      {/* Kanban */}
      {snapshot && totalCount > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
          {snapshot.stage_order.map((s) => (
            <StageColumn key={s} stage={s}
              records={snapshot.stages[s] || []}
              onCardClick={setSelectedRecord}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onSelectAllInStage={selectAllInStage} />
          ))}
        </div>
      )}

      {/* Bulk action bar · floats at bottom when ≥1 card selected */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          onClear={clearSelection}
          onBulkAdvance={() => setBulkDialog('advance')}
          onBulkJourney={() => setBulkDialog('journey')} />
      )}

      {bulkDialog && (
        <BulkAdvanceDialog
          mode={bulkDialog}
          records={(snapshot?.stage_order || []).flatMap((s) => (snapshot.stages[s] || []))
            .filter((r) => selectedIds.has(r._id))}
          onClose={() => setBulkDialog(null)}
          onDone={() => { setBulkDialog(null); clearSelection(); load(); }} />
      )}

      {selectedRecord && (
        <AdvanceDrawer record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onAdvanced={load} />
      )}
    </div>
  );
}


// ─── Bulk action UI ────────────────────────────────────────────────────────

const STAGE_LABEL = {
  approval_pending:  '1 · Approval pending',
  ordered:           '2 · Ordered',
  awaiting_ready:    '3 · Awaiting ready',
  ready_to_dispatch: '4 · Ready to dispatch',
  couriered:         '5 · Couriered',
  at_dbr_depot:      '6 · DBR depot received',
};
const STAGE_ORDER = ['approval_pending', 'ordered', 'awaiting_ready',
  'ready_to_dispatch', 'couriered', 'at_dbr_depot'];
const NEXT_STAGE = STAGE_ORDER.reduce((acc, s, i) => {
  acc[s] = STAGE_ORDER[i + 1] || null;
  return acc;
}, {});

function BulkActionBar({ count, onClear, onBulkAdvance, onBulkJourney }) {
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full shadow-xl px-5 py-3 flex items-center gap-3"
      style={{ background: '#15110d', color: '#f3e6cf' }}
      data-testid="gift-ops-bulk-bar"
    >
      <span className="text-sm font-semibold">
        <span data-testid="bulk-selected-count">{count}</span> selected
      </span>
      <span className="opacity-30">·</span>
      <button onClick={onBulkAdvance}
        className="text-sm font-semibold px-3 py-1 rounded-full"
        style={{ background: '#FF6900', color: 'white' }}
        data-testid="bulk-advance-btn">
        → Advance Kanban stage
      </button>
      <button onClick={onBulkJourney}
        className="text-sm font-semibold px-3 py-1 rounded-full"
        style={{ background: '#0F766E', color: 'white' }}
        data-testid="bulk-journey-btn">
        🛣 Bulk-tick journey step
      </button>
      <button onClick={onClear}
        className="text-xs opacity-70 hover:opacity-100"
        data-testid="bulk-clear-btn">
        ✕ Clear
      </button>
    </div>
  );
}


function BulkAdvanceDialog({ mode, records, onClose, onDone }) {
  const byStage = useMemo(() => {
    const m = {};
    for (const r of records) {
      (m[r.stage] = m[r.stage] || []).push(r);
    }
    return m;
  }, [records]);
  const distinctStages = Object.keys(byStage);
  const canAdvanceKanban = mode === 'advance' && distinctStages.length === 1 && NEXT_STAGE[distinctStages[0]];

  const [step, setStep] = useState(7);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const runAdvanceKanban = async () => {
    if (!canAdvanceKanban) return;
    setBusy(true);
    let ok = 0;
    let fail = 0;
    for (const r of records) {
      try {
        await axios.post(`${API}/xiaomi/gift-ops/${r._id}/advance`,
          { to_stage: NEXT_STAGE[r.stage], payload: {} },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        ok++;
      } catch (e) { fail++; }
    }
    toast.success(`Advanced ${ok} of ${records.length} · ${fail} failed`,
      { duration: 5000 });
    setBusy(false);
    onDone();
  };

  const runBulkJourney = async () => {
    setBusy(true);
    try {
      const rows = records.map((r) => ({
        retailer_id: r.dealer_id,
        benefit_id: r.benefit_id,
        month_ym: r.month_ym,
        step: Number(step),
        note: note.trim() || null,
      }));
      const r = await axios.post(`${API}/xiaomi/journey/bulk-advance`,
        { rows },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const d = r.data;
      toast.success(`${d.marked} steps marked · ${d.errors} error(s)`,
        { duration: 5000 });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk-tick failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="bulk-advance-dialog">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-line p-5">
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
            {mode === 'advance' ? 'Bulk advance Kanban stage' : 'Bulk-tick 15-step journey'}
          </h3>
          <p className="text-[12px] text-soft mt-1">
            {records.length} partner record{records.length === 1 ? '' : 's'} selected.
          </p>
        </div>
        <div className="p-5 space-y-4">
          {mode === 'advance' ? (
            <>
              <div className="text-[12px]">
                <strong>Current stages in your selection:</strong>
                <ul className="mt-1 space-y-1">
                  {distinctStages.map((s) => (
                    <li key={s} className="flex items-center gap-2">
                      <span className="text-soft">{byStage[s].length}×</span>
                      <span>{STAGE_LABEL[s] || s}</span>
                      <span className="text-soft">→</span>
                      <span style={{ color: NEXT_STAGE[s] ? '#FF6900' : '#999', fontWeight: 600 }}>
                        {NEXT_STAGE[s] ? STAGE_LABEL[NEXT_STAGE[s]] : 'already final stage'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              {!canAdvanceKanban && (
                <div className="text-[11px] p-3 rounded"
                  style={{ background: '#FEF3C7', color: '#92400E' }}>
                  {distinctStages.length > 1
                    ? 'Mixed stages selected — please filter to one column before bulk-advancing the Kanban. Use "🛣 Bulk-tick journey step" instead to tick the same journey step for everyone, irrespective of Kanban stage.'
                    : 'Selected records are already at the final stage. Use "🛣 Bulk-tick journey step" to advance the chase / feedback steps.'}
                </div>
              )}
              <p className="text-[11px] text-soft">
                Note: bulk-advance Kanban does NOT capture per-partner supplier / courier
                fields. Open individual drawers for those — bulk advance is meant for
                same-day batch transitions (e.g. "today these 22 were couriered together
                via DTDC").
              </p>
            </>
          ) : (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-soft">
                  Journey step number (1–15)
                </label>
                <input type="number" min="1" max="15" value={step}
                  onChange={(e) => setStep(e.target.value)}
                  className="w-full px-3 py-2 rounded border border-line text-sm mt-1"
                  data-testid="bulk-step-input" />
                <p className="text-[10px] text-soft mt-1">
                  Common: 7=Day 1 WA, 8=Day 2 WA, 9=Phone, 11=Day 15 escalation,
                  12=Feedback request, 15=Closed.
                </p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-soft">
                  Optional note (applied to all)
                </label>
                <input type="text" value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. AWB range 7281234001-50, DTDC"
                  className="w-full px-3 py-2 rounded border border-line text-sm mt-1"
                  data-testid="bulk-note-input" />
              </div>
            </>
          )}
        </div>
        <div className="border-t border-line p-4 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="px-4 py-2 rounded text-sm border border-line bg-white hover:bg-gray-50"
            data-testid="bulk-cancel-btn">Cancel</button>
          {mode === 'advance' ? (
            <button onClick={runAdvanceKanban}
              disabled={!canAdvanceKanban || busy}
              className="px-4 py-2 rounded text-sm text-white font-semibold disabled:opacity-40"
              style={{ background: '#FF6900' }}
              data-testid="bulk-advance-confirm">
              {busy ? 'Advancing…' : `Advance all ${records.length}`}
            </button>
          ) : (
            <button onClick={runBulkJourney} disabled={busy || !step}
              className="px-4 py-2 rounded text-sm text-white font-semibold disabled:opacity-40"
              style={{ background: '#0F766E' }}
              data-testid="bulk-journey-confirm">
              {busy ? 'Marking…' : `Tick Step ${step} for ${records.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

